import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, formatBytes, formatDate } from '../lib/api.js';
import AdminShell from './AdminShell.jsx';
import BrandingPanel from './BrandingPanel.jsx';

export default function Dashboard() {
  const [galleries, setGalleries] = useState([]);
  const [creating, setCreating] = useState(false);
  const [showBranding, setShowBranding] = useState(false);
  const [created, setCreated] = useState(null);
  const [error, setError] = useState('');

  const load = () => api.get('/api/admin/galleries').then(setGalleries).catch((e) => setError(e.message));
  useEffect(() => {
    load();
  }, []);

  const totalBytes = galleries.reduce((n, g) => n + (g.stats?.bytes || 0), 0);

  return (
    <AdminShell
      actions={
        <>
          <Link className="btn sm" to="/admin/library">
            Library
          </Link>
          <button className="sm" onClick={() => setShowBranding((v) => !v)}>
            Branding
          </button>
          <button className="primary sm" onClick={() => setCreating(true)}>
            New gallery
          </button>
        </>
      }
    >
      {error && <div className="error" style={{ marginBottom: '1rem' }}>{error}</div>}

      {showBranding && (
        <div style={{ marginBottom: '1.25rem' }}>
          <BrandingPanel onClose={() => setShowBranding(false)} />
        </div>
      )}

      <div className="stat-grid" style={{ marginBottom: '1.25rem' }}>
        <div className="stat">
          <b>{galleries.length}</b>
          <span className="tiny">Galleries</span>
        </div>
        <div className="stat">
          <b>{galleries.filter((g) => g.status === 'delivered' && !g.expired).length}</b>
          <span className="tiny">Live</span>
        </div>
        <div className="stat">
          <b>{formatBytes(totalBytes)}</b>
          <span className="tiny">Stored in R2</span>
        </div>
        <div className="stat">
          <b>{galleries.reduce((n, g) => n + g.view_count, 0)}</b>
          <span className="tiny">Total views</span>
        </div>
      </div>

      {creating && (
        <NewGalleryForm
          onCancel={() => setCreating(false)}
          onCreated={(g) => {
            setCreating(false);
            setCreated(g);
            load();
          }}
        />
      )}

      {created && (
        <div className="card stack" style={{ marginBottom: '1rem' }}>
          <div className="spread">
            <h2>Gallery created — save these details now</h2>
            <button className="ghost sm" onClick={() => setCreated(null)}>
              Dismiss
            </button>
          </div>
          <p className="muted" style={{ margin: 0 }}>
            The password is shown once and is not recoverable. You can always set a new one later.
          </p>
          <CopyRow label="Link" value={`${window.location.origin}/${created.slug}`} />
          <CopyRow label="Password" value={created.password} />
          {created.access_code && <CopyRow label="TV / phone app code" value={created.access_code} />}
          <Link className="btn primary" to={`/admin/galleries/${created.id}`}>
            Upload photos and video
          </Link>
        </div>
      )}

      <div className="gallery-list">
        {galleries.map((g) => (
          <Link key={g.id} className="card gallery-row" to={`/admin/galleries/${g.id}`}>
            <div className="spread">
              <div>
                <h2 style={{ marginBottom: '0.15rem' }}>{g.client_name}</h2>
                <div className="tiny">{formatDate(g.wedding_date)}</div>
              </div>
              <StatusBadge gallery={g} />
            </div>
            <div className="row tiny" style={{ marginTop: '0.75rem' }}>
              <span>{g.stats.photos} photos</span>
              <span>·</span>
              <span>{g.stats.videos} video{g.stats.videos === 1 ? '' : 's'}</span>
              <span>·</span>
              <span>{formatBytes(g.stats.bytes)}</span>
              <span>·</span>
              <span>
                {g.view_count === 0 ? 'Not viewed yet' : `${g.view_count} views, last ${formatDate(g.last_viewed_at)}`}
              </span>
              {g.access_code && (
                <>
                  <span>·</span>
                  <span title="TV / phone app code">
                    App code <code className="access-code-inline">{g.access_code}</code>
                    {!g.access_code_enabled && ' (off)'}
                  </span>
                </>
              )}
            </div>
          </Link>
        ))}
        {galleries.length === 0 && !creating && (
          <div className="card muted">No galleries yet. Create one to get started.</div>
        )}
      </div>
    </AdminShell>
  );
}

export function StatusBadge({ gallery }) {
  if (gallery.expired) return <span className="badge red">Expired</span>;
  if (gallery.status === 'delivered') return <span className="badge">Delivered</span>;
  return <span className="badge grey">Draft</span>;
}

export function CopyRow({ label, value }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="field" style={{ marginBottom: 0 }}>
      <label>{label}</label>
      <div className="row" style={{ flexWrap: 'nowrap' }}>
        <input readOnly value={value} onFocus={(e) => e.target.select()} />
        <button
          type="button"
          className="sm"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(value);
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            } catch {
              /* clipboard blocked (non-https or denied) - the field is selectable as a fallback */
            }
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
    </div>
  );
}

function NewGalleryForm({ onCancel, onCreated }) {
  const [form, setForm] = useState({ client_name: '', wedding_date: '', expires_at: '', password: '', welcome_message: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      onCreated(await api.post('/api/admin/galleries', form));
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  }

  return (
    <form className="card stack" style={{ marginBottom: '1rem' }} onSubmit={submit}>
      <h2>New gallery</h2>
      <div className="field">
        <label htmlFor="cn">Client name</label>
        <input id="cn" autoFocus required value={form.client_name} onChange={set('client_name')} placeholder="Thandi &amp; Sipho" />
      </div>
      <div className="row" style={{ alignItems: 'flex-start' }}>
        <div className="field grow">
          <label htmlFor="wd">Wedding date</label>
          <input id="wd" type="date" value={form.wedding_date} onChange={set('wedding_date')} />
        </div>
        <div className="field grow">
          <label htmlFor="ex">Expires (optional)</label>
          <input id="ex" type="date" value={form.expires_at} onChange={set('expires_at')} />
        </div>
      </div>
      <div className="field">
        <label htmlFor="pw">Password (leave blank to generate one)</label>
        <input id="pw" value={form.password} onChange={set('password')} placeholder="auto-generated" />
      </div>
      <div className="field">
        <label htmlFor="wm">Welcome note (optional)</label>
        <textarea id="wm" rows={2} value={form.welcome_message} onChange={set('welcome_message')} />
      </div>
      {error && <div className="error">{error}</div>}
      <div className="row">
        <button className="primary" disabled={busy}>
          {busy ? 'Creating…' : 'Create gallery'}
        </button>
        <button type="button" className="ghost" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}
